import React, { useState, useEffect } from "react";
import { Button } from "@mui/material";
import { useFormik, FormikProvider } from "formik";
import { Grid, Box } from "@mui/material";
import InputsFormFields from "./InputsFormFields";

import moment from "moment";
import {
  createInputs,
  modifyInputs,
  getFilterByRolls,
} from "../functions";
import { useSelector, useDispatch } from "react-redux";
import { showMessage } from "store/modules/Message";

const InputsForm = ({ mode, open, handleClose, handleCancel, onDirtyChange }) => {
  const { defaultData } = useSelector(({ inputs }) => inputs);
  const [isMenuForm, setIsMenuForm] = useState(null);
  const dispatch = useDispatch();
  const UNIT_OPTIONS = [
    { id: 1, name: "Pieza" },
    { id: 2, name: "Caja" },
    { id: 3, name: "Bolsa" },
    { id: 4, name: "Rollo" },
  ];

  const messages = (mensaje) => {
    dispatch(
      showMessage({
        message:
          mensaje === "success"
            ? mode === "POST"
              ? "La entrada se ha registrado con éxito"
              : "La entrada se ha actualizado con éxito"
            : "Ocurrió un error al momento de guardar la entrada",
        variant: mensaje,
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      })
    );
  };

  const formik = useFormik({
    initialValues: {
      date: moment().format("YYYY-MM-DD"),
      description: "",
      code: "",
      amount: 0,
      kg: "",
      meters: "",
      rolls: [],
      machinary: [
        {
          id: 0,
          code: "",
          is_void: false,
          dateMaintenance: moment().format("YYYY-MM-DD"),
        },
      ],
      category: null,
      supplier: null,
      unit: null,
    },
    onSubmit: async (values) => {
      const submitValues = { ...values };
      if (submitValues.unit && typeof submitValues.unit === "object")
        submitValues.unit = submitValues.unit.name;
      if (mode === "POST") {
        const success = await createInputs(submitValues, isMenuForm?.name);
        messages(success ? "success" : "error");
      } else {
        const success = await modifyInputs(submitValues, isMenuForm?.name);
        messages(success ? "success" : "error");
      }
      handleClose();
    },
  });

  // Inicializar formulario en modo edición
  useEffect(() => {
    if (!open) return;
    if (mode === "PUT" && defaultData?.id) {
      setIsMenuForm(defaultData.category || null);
      // Reset inmediato con los campos básicos (id disponible antes de la respuesta async)
      formik.resetForm({
        values: {
          id: defaultData.id,
          date: defaultData.date || moment().format("YYYY-MM-DD"),
          description: defaultData.description || "",
          amount: defaultData.amount || 0,
          kg: defaultData.kg || "",
          meters: defaultData.meters || "",
          code: defaultData.code || "",
          category: defaultData.category || null,
          supplier: defaultData.supplier || null,
          unit: UNIT_OPTIONS.find((u) => u.name === defaultData.unit) || null,
          rolls: [],
          machinary: [],
        },
      });
      // Cargar rollos de la API y completar el formulario
      getFilterByRolls(defaultData.id).then((rolls) => {
        const mapped = (rolls || []).map((r) => ({
          id: r.id,
          cloth: r.cloth,
          code: r.code,
          color: r.color || "",
          totalKg: r.totalKg ?? "",
          meters: r.meters ?? "",
        }));
        formik.setFieldValue("rolls", mapped);
        formik.setFieldValue("amount", mapped.length);
      });
    } else if (mode === "POST") {
      setIsMenuForm(null);
      formik.resetForm({
        values: {
          date: moment().format("YYYY-MM-DD"),
          description: "",
          code: "",
          amount: 0,
          kg: "",
          meters: "",
          rolls: [],
          machinary: [
            {
              id: 0,
              code: "",
              is_void: false,
              dateMaintenance: moment().format("YYYY-MM-DD"),
            },
          ],
          category: null,
          supplier: null,
          unit: null,
        },
      });
    }
  }, [mode, defaultData?.id, open]);

  // Valor derivado primitivo: solo cambia cuando kg/metros de los rollos cambian realmente
  const rollsTotals = (formik.values.rolls || [])
    .map((r) => `${r.totalKg ?? ""}|${r.meters ?? ""}`)
    .join(",");

  // Auto-calcular kg y metros totales a partir de los rollos
  useEffect(() => {
    if (isMenuForm?.name === "Telas") {
      const rolls = formik.values.rolls || [];
      const totalKg = rolls.reduce(
        (acc, r) => acc + (parseFloat(r.totalKg) || 0),
        0
      );
      const totalMeters = rolls.reduce(
        (acc, r) => acc + (parseFloat(r.meters) || 0),
        0
      );
      formik.setFieldValue("kg", parseFloat(totalKg.toFixed(3)));
      formik.setFieldValue("meters", parseFloat(totalMeters.toFixed(3)));
    }
  }, [rollsTotals, isMenuForm?.name]);

  // Notificar al padre cuando hay cambios sin guardar
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(formik.dirty);
  }, [formik.dirty]);

  const { setFieldValue } = formik;
  return (
    <FormikProvider value={formik}>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "#fafafa",
          ".dark &": { background: "#0f172a" },
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <InputsFormFields
            formik={formik}
            mode={mode}
            setFieldValue={setFieldValue}
            setIsMenuForm={setIsMenuForm}
            isMenuForm={isMenuForm}
          />
        </Box>
        <Box sx={{
          px: 4, py: 2.5,
          borderTop: "1px solid rgba(244,63,94,0.15)",
          display: "flex", justifyContent: "flex-end", gap: 1.5,
          background: "#fff",
          ".dark &": { background: "#1e293b", borderColor: "rgba(244,63,94,0.2)" },
        }}>
          <Button
            onClick={handleCancel || handleClose}
            variant="outlined"
            sx={{
              fontFamily: "'Poppins', sans-serif", fontWeight: 600, borderRadius: 2,
              borderColor: "#f43f5e", color: "#f43f5e",
              "&:hover": { borderColor: "#e11d48", background: "#fff0f6" },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={!isMenuForm?.name}
            sx={{
              fontFamily: "'Poppins', sans-serif", fontWeight: 600, borderRadius: 2,
              background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
              boxShadow: "0 4px 14px rgba(244,63,94,0.35)",
              "&:hover": { background: "linear-gradient(135deg, #e11d48 0%, #db2777 100%)" },
              "&.Mui-disabled": { background: "rgba(244,63,94,0.3)", color: "#fff" },
            }}
          >
            Guardar entrada
          </Button>
        </Box>
      </Box>
    </FormikProvider>
  );
};

export default InputsForm;
